/**
 * useGuestGoalOperations — Goal CRUD for unauthenticated users (localStorage-backed).
 */
import { useState, useEffect } from "react";
import { Goal } from "@/types/todo";
import { useGuestStorage } from "./useGuestStorage";
import { GOALS_CHANGED_EVENT } from "@/lib/bucketList";

export const useGuestGoalOperations = () => {
  const { loadGoals, saveGoals } = useGuestStorage();
  const [goals, setGoals] = useState<Goal[]>(() => loadGoals());

  // Save goals to localStorage whenever they change
  useEffect(() => {
    saveGoals(goals);
  }, [goals]);

  // Pick up items saved elsewhere (e.g. "Add to Bucket List" on a place).
  useEffect(() => {
    const reload = () => setGoals(loadGoals());
    window.addEventListener(GOALS_CHANGED_EVENT, reload);
    return () => window.removeEventListener(GOALS_CHANGED_EVENT, reload);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const addGoal = (goal: Goal) => {
    setGoals(prevGoals => [...prevGoals, { ...goal, id: crypto.randomUUID() }]);
  };

  const deleteGoal = (goalToDelete: Goal) => {
    setGoals(prevGoals => prevGoals.filter(goal => goal.id !== goalToDelete.id));
  };

  const updateGoal = (updatedGoal: Goal) => {
    setGoals(prevGoals =>
      prevGoals.map(goal => (goal.id === updatedGoal.id ? updatedGoal : goal))
    );
  };

  return {
    goals,
    setGoals,
    addGoal,
    deleteGoal,
    updateGoal
  };
};
